import { Component, computed, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, fromEvent, map, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../auth/auth.service';
import { NachrichtenService } from '../../services/nachrichten.service';
import { ToastComponent } from '@shared/components/toast.component';
import { ConfirmSnackbarComponent } from '@shared/components/confirm-snackbar.component';
import { NavIconComponent } from './components/nav-icon/nav-icon.component';
import type { NavIconName } from './components/nav-icon/nav-icon.component';

export interface NavItem {
  id: string;
  route: string;
  exact?: boolean;
  cssClass: string;
  labelKey: string;
  icon: NavIconName;
  badge?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'home', route: '/', exact: true, cssClass: 'nav-home', labelKey: 'nav.home', icon: 'home' },
  { id: 'hilfe', route: '/hilfe', cssClass: 'nav-hilfe', labelKey: 'nav.hilfe', icon: 'hilfe' },
  {
    id: 'marktplatz',
    route: '/marktplatz',
    cssClass: 'nav-marktplatz',
    labelKey: 'nav.marktplatz',
    icon: 'marktplatz',
  },
  {
    id: 'verzeichnis',
    route: '/verzeichnis',
    cssClass: 'nav-verzeichnis',
    labelKey: 'nav.verzeichnis',
    icon: 'verzeichnis',
  },
  {
    id: 'nachrichten',
    route: '/nachrichten',
    cssClass: 'nav-nachrichten nav-badge-wrap',
    labelKey: 'nav.nachrichten',
    icon: 'nachrichten',
    badge: true,
  },
  {
    id: 'profil',
    route: '/profil',
    cssClass: 'nav-profil',
    labelKey: 'nav.profil',
    icon: 'profil',
  },
];

const ADMIN_NAV_ITEM: NavItem = {
  id: 'admin',
  route: '/admin',
  cssClass: 'nav-admin',
  labelKey: 'nav.admin',
  icon: 'admin',
};

@Component({
  selector: 'app-layout',
  imports: [
    NgOptimizedImage,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    ToastComponent,
    ConfirmSnackbarComponent,
    NavIconComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent {
  auth = inject(AuthService);
  private router = inject(Router);
  private nachrichtenService = inject(NachrichtenService);

  readonly navItems = computed<NavItem[]>(() =>
    this.auth.canAccessAdmin() ? [...NAV_ITEMS, ADMIN_NAV_ITEM] : NAV_ITEMS,
  );

  readonly unreadNachrichten = toSignal(
    this.nachrichtenService.unreadCounts$.pipe(map((counts) => counts.total)),
    { initialValue: 0 },
  );

  get isAuthPage(): boolean {
    const url = this.router.url;
    return (
      url.startsWith('/login') ||
      url.startsWith('/register') ||
      url.startsWith('/verification-pending')
    );
  }

  constructor() {
    this.refreshUnreadCountsOnLogin();
    this.reloadProfileOnNavigation();
    this.refreshWhenTabBecomesVisible();
  }

  private refreshUnreadCountsOnLogin() {
    effect(() => {
      if (this.auth.isLoggedIn()) untracked(() => this.nachrichtenService.refreshUnreadCounts());
    });
  }

  private reloadProfileOnNavigation() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd && this.auth.isLoggedIn()),
        switchMap(() => this.auth.loadProfile()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private refreshWhenTabBecomesVisible() {
    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible' && this.auth.isLoggedIn()),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.nachrichtenService.refreshUnreadCounts();
        this.auth.refreshProfileIfStale();
      });
  }

  logout() {
    this.auth.logout();
    this.router.navigate(['/login']);
  }
}
